<script lang="ts" setup>
/* Utils */
import { checkSavedCard } from '@/utils/checkSavedCard'

/* Types */
import { BasketPayment } from '@/types/api/api-v3/paymentMethods'
import { DeliveryMethods } from '@/types/api/api-v3/basket'

/* Components */
import BasketPaymentListItem from '@/components/Basket/basket-payment/BasketPaymentListItem.vue'
import IconArrowLeft from '@/components/Icons/IconArrowLeft.vue'
import IconArrowRight from '@/components/Icons/IconArrowRight.vue'

type Props = {
  basketPaymentMethods: BasketPayment[]
  methodDelivery: DeliveryMethods
  selectedPaymentMethod: BasketPayment | null
}
const props = defineProps<Props>()

const { isMobile } = useDevice()

const emit = defineEmits(['update:selected-payment'])

const selectPaymentMethod = (paymentMethod: BasketPayment) =>
  emit('update:selected-payment', paymentMethod)

const selectedPaymentMethodType = computed(
  () => props.selectedPaymentMethod?.type,
)

const selectedSavedCardId = computed(() => {
  if (checkSavedCard(props.selectedPaymentMethod)) {
    return props.selectedPaymentMethod.id
  }

  return null
})

const checkActivePaymentItem = (paymentItem: BasketPayment) => {
  if (checkSavedCard(paymentItem)) {
    return paymentItem.id === selectedSavedCardId.value
  }

  return paymentItem.type === selectedPaymentMethodType.value
}

const paymentsList = computed(() => {
  if (!props.basketPaymentMethods) {
    return []
  }

  const list = []

  for (const payment of props.basketPaymentMethods) {
    list.push({
      payment,
      isActive: checkActivePaymentItem(payment),
    })
  }

  return list
})
</script>

<template>
  <!-- TODO заменить на AppSlider из ui-layer -->
  <v-slide-group
    v-if="!isMobile"
    show-arrows
    :prev-icon="IconArrowLeft"
    :next-icon="IconArrowRight"
  >
    <v-slide-group-item
      v-for="paymentItem in paymentsList"
      :key="paymentItem.payment.type"
    >
      <basket-payment-list-item
        :payment-item="paymentItem.payment"
        :is-active="paymentItem.isActive"
        :method-delivery="methodDelivery"
        @update:selected-payment="selectPaymentMethod"
      />
    </v-slide-group-item>
  </v-slide-group>

  <div v-else class="payment-list">
    <basket-payment-list-item
      v-for="paymentItem in paymentsList"
      :key="paymentItem.payment.type"
      :payment-item="paymentItem.payment"
      :is-active="paymentItem.isActive"
      :method-delivery="methodDelivery"
      @update:selected-payment="selectPaymentMethod"
    />
  </div>
</template>

<style lang="scss" scoped>
.payment-list {
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  padding: 24px 20px;
  margin: 0 -20px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-padding-inline: 20px;

  &::-webkit-scrollbar {
    display: none;
  }
}

.v-slide-group {
  position: relative !important;
  overflow: unset;

  :deep() {
    .v-slide-group__content {
      gap: 10px;
      padding: 25px 0 !important;
    }

    .v-slide-group__next {
      right: -20px;
    }

    .v-slide-group__prev {
      left: -20px;
    }

    .v-slide-group__prev,
    .v-slide-group__next {
      position: absolute;
      top: 0;
      z-index: 1;
      min-width: unset;
      height: 100%;

      @include media('<=tablet') {
        display: none;
      }

      &--disabled {
        display: none;
      }

      .v-icon {
        top: 0;
        width: 40px;
        height: 40px;
        background-color: $white;
        border-radius: 50%;
        box-shadow: 0 4px 20px rgba(65, 58, 91, 0.15);
      }

      .v-icon__component {
        width: 20px;
        height: 16px;

        path {
          fill: #2c2f39;
        }
      }
    }
  }
}
</style>
